import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import AccountCancellation from '@/components/AccountCancellation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Building2, Palette, Phone, Globe, CreditCard, Eye, EyeOff, Mail, MessageSquare, Moon, Sun, Check, Music, DollarSign, Plus, X, Bell, UserX, Send, Loader2, Lock, Key, Link2, Play, AlertCircle, Video, ExternalLink, Clock, Save, Database, Camera, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme, themes } from '@/contexts/ThemeContext';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (1:30 PM)' },
  { value: '24h', label: '24-hour / Military (13:30)' }
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2024)' },
  { value: 'MMMM DD, YYYY', label: 'MMMM DD, YYYY (December 31, 2024)' }
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Asia/Shanghai', label: 'China (CST)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' }
];

export default function SettingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const { theme: currentTheme, setTheme } = useTheme();
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({
    company_name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    primary_color: '#06b6d4',
    secondary_color: '#8b5cf6',
    facebook_url: '',
    instagram_url: '',
    twitter_url: '',
    custom_domain: '',
    // Date/Time Format Settings
    time_format: '12h',
    date_format: 'MM/DD/YYYY',
    timezone: 'America/New_York',
    business_hours: {
      monday: { enabled: true, open: '09:00', close: '17:00' },
      tuesday: { enabled: true, open: '09:00', close: '17:00' },
      wednesday: { enabled: true, open: '09:00', close: '17:00' },
      thursday: { enabled: true, open: '09:00', close: '17:00' },
      friday: { enabled: true, open: '09:00', close: '17:00' },
      saturday: { enabled: false, open: '10:00', close: '14:00' },
      sunday: { enabled: false, open: '10:00', close: '14:00' }
    },
    tax_id: '',
    payment_processor: '',
    // Stripe
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    // Square
    square_application_id: '',
    square_access_token: '',
    square_location_id: '',
    square_environment: 'sandbox',
    // PayPal
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_environment: 'sandbox',
    // Venmo
    venmo_client_id: '',
    venmo_client_secret: '',
    venmo_environment: 'sandbox',
    // Email
    email_provider: '',
    sendgrid_api_key: '',
    sendgrid_from_email: '',
    sendgrid_from_name: '',
    resend_api_key: '',
    resend_from_email: '',
    resend_from_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    // SMS
    sms_provider: '',
    twilio_account_sid: '',
    twilio_auth_token: '',
    twilio_phone_number: '',
    twilio_messaging_service_sid: '',
    vonage_api_key: '',
    vonage_api_secret: '',
    vonage_phone_number: '',
    vonage_application_id: '',
    vonage_private_key: '',
    // Spotify
    spotify_client_id: '',
    spotify_client_secret: '',
    // Payment Types
    payment_types: ['Cash', 'Check', 'Credit Card', 'Venmo', 'PayPal', 'Zelle', 'Bank Transfer'],
    // Notification Settings
    notify_new_lead: 'email',
    notify_client_login: 'none',
    notify_invoice_payment: 'email',
    notify_payment_received: 'email',
    notify_contract_signed: 'email',
    notify_form_edited: 'email',
    // Webhook Settings
    webhook_enabled: false,
    webhook_booking_created: '',
    webhook_booking_updated: '',
    webhook_contract_signed: '',
    webhook_payment_received: '',
    webhook_form_submitted: '',
    webhook_guest_request: '',
    webhook_lead_created: '',
    webhook_message_received: '',
    // Meeting Integration Settings
    meeting_provider: '',
    zoom_account_id: '',
    zoom_client_id: '',
    zoom_client_secret: '',
    google_client_id: '',
    google_client_secret: '',
    google_refresh_token: '',
    teams_client_id: '',
    teams_client_secret: '',
    teams_tenant_id: '',
    auto_generate_meeting_links: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [showSecrets, setShowSecrets] = useState({});
  const [newPaymentType, setNewPaymentType] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testSmsPhone, setTestSmsPhone] = useState('');
  const [sendingTestSms, setSendingTestSms] = useState(false);
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState({ current: false, new: false, confirm: false });
  
  // Webhook test state
  const [testingWebhook, setTestingWebhook] = useState(null);
  
  // Meeting integration test state
  const [testingMeeting, setTestingMeeting] = useState(null);
  const [googleAuthUrl, setGoogleAuthUrl] = useState('');

  const defaultBusinessHours = {
    monday: { enabled: true, open: '09:00', close: '17:00' },
    tuesday: { enabled: true, open: '09:00', close: '17:00' },
    wednesday: { enabled: true, open: '09:00', close: '17:00' },
    thursday: { enabled: true, open: '09:00', close: '17:00' },
    friday: { enabled: true, open: '09:00', close: '17:00' },
    saturday: { enabled: false, open: '10:00', close: '14:00' },
    sunday: { enabled: false, open: '10:00', close: '14:00' }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      // Merge fetched settings with defaults, especially for business_hours
      const fetchedSettings = response.data;
      if (!fetchedSettings.business_hours || typeof fetchedSettings.business_hours === 'string') {
        fetchedSettings.business_hours = defaultBusinessHours;
      }
      setSettings(fetchedSettings);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current) {
      toast.error('Please enter your current password');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success('Password changed successfully!');
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1 * 1024 * 1024) {
        toast.error('Logo size should be less than 1MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setSettings({ ...settings, logo: base64String });
        setLogoPreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings);
      toast.success('Settings saved successfully');
      // Refresh settings context to update date/time formatting across the app
      refreshSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!settings.email_provider) {
      toast.error('Please select an email provider first');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      // Save settings first to ensure the latest config is used
      await axios.put(`${API}/settings`, settings);
      
      // Send test email
      const response = await axios.post(`${API}/email/test?test_email=${encodeURIComponent(testEmailAddress)}`);
      toast.success(response.data.message || 'Test email sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const sendTestSms = async () => {
    if (!testSmsPhone || testSmsPhone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    if (!settings.sms_provider) {
      toast.error('Please select an SMS provider first');
      return;
    }
    
    // Validate credentials based on provider
    if (settings.sms_provider === 'twilio') {
      if (!settings.twilio_account_sid || !settings.twilio_auth_token || !settings.twilio_phone_number) {
        toast.error('Please fill in all Twilio credentials first');
        return;
      }
    } else if (settings.sms_provider === 'vonage') {
      // Check if Messages API credentials are provided (preferred)
      const hasMessagesApi = settings.vonage_application_id && settings.vonage_private_key;
      // Or SMS API credentials
      const hasSmsApi = settings.vonage_api_key && settings.vonage_api_secret;
      
      if (!hasMessagesApi && !hasSmsApi) {
        toast.error('Please fill in Vonage credentials: either Application ID + Private Key (recommended), or API Key + API Secret');
        return;
      }
      if (!settings.vonage_phone_number) {
        toast.error('Please enter your Vonage phone number');
        return;
      }
    }
    
    setSendingTestSms(true);
    try {
      // Save settings first to ensure the latest config is used
      await axios.put(`${API}/settings`, settings);
      
      // Send test SMS
      const response = await axios.post(`${API}/sms/test?test_phone=${encodeURIComponent(testSmsPhone)}`);
      if (response.data.success) {
        toast.success(response.data.message || 'Test SMS sent successfully!');
      } else {
        toast.warning(response.data.message || 'SMS sent but delivery uncertain');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test SMS');
    } finally {
      setSendingTestSms(false);
    }
  };

  const addPaymentType = () => {
    if (!newPaymentType.trim()) return;
    if (settings.payment_types?.includes(newPaymentType.trim())) {
      toast.error('Payment type already exists');
      return;
    }
    setSettings({
      ...settings,
      payment_types: [...(settings.payment_types || []), newPaymentType.trim()]
    });
    setNewPaymentType('');
  };

  const removePaymentType = (type) => {
    setSettings({
      ...settings,
      payment_types: (settings.payment_types || []).filter(t => t !== type)
    });
  };

  const testWebhook = async (eventType, webhookUrl) => {
    if (!webhookUrl) {
      toast.error('Please enter a webhook URL first');
      return;
    }
    
    setTestingWebhook(eventType);
    try {
      const response = await axios.post(`${API}/settings/test-webhook`, {
        event_type: eventType,
        webhook_url: webhookUrl
      });
      
      if (response.data.success) {
        toast.success(`Test webhook sent! Status: ${response.data.status_code}`);
      } else {
        toast.error(response.data.error || 'Webhook test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test webhook');
    } finally {
      setTestingWebhook(null);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div data-testid="settings-page">
        <div className="p-4 md:p-8">
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="settings-heading">Settings</h1>
            <p className="text-muted-foreground">Customize your company information and branding</p>
          </div>

          <Tabs defaultValue="theme" className="space-y-6">
            <TabsList className="glass-card border-glass-border flex-wrap">
              <TabsTrigger value="theme" data-testid="tab-theme">
                <Palette className="w-4 h-4 mr-2" />
                Theme
              </TabsTrigger>
              <TabsTrigger value="company" data-testid="tab-company">
                <Building2 className="w-4 h-4 mr-2" />
                Company Info
              </TabsTrigger>
              <TabsTrigger value="branding" data-testid="tab-branding">
                <Palette className="w-4 h-4 mr-2" />
                Branding
              </TabsTrigger>
              <TabsTrigger value="contact" data-testid="tab-contact">
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </TabsTrigger>
              <TabsTrigger value="social" data-testid="tab-social">
                <Globe className="w-4 h-4 mr-2" />
                Social Media
              </TabsTrigger>
              <TabsTrigger value="payment" data-testid="tab-payment">
                <CreditCard className="w-4 h-4 mr-2" />
                Payment
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="sms" data-testid="tab-sms">
                <MessageSquare className="w-4 h-4 mr-2" />
                SMS/Text
              </TabsTrigger>
              <TabsTrigger value="notifications" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="spotify" data-testid="tab-spotify">
                <Music className="w-4 h-4 mr-2" />
                Spotify
              </TabsTrigger>
              <TabsTrigger value="payment-types" data-testid="tab-payment-types">
                <DollarSign className="w-4 h-4 mr-2" />
                Payment Types
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security">
                <Lock className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="webhooks" data-testid="tab-webhooks">
                <Link2 className="w-4 h-4 mr-2" />
                Webhooks / Zapier
              </TabsTrigger>
              <TabsTrigger value="meetings" data-testid="tab-meetings">
                <Video className="w-4 h-4 mr-2" />
                Meeting Integrations
              </TabsTrigger>
              <TabsTrigger value="client-portal" data-testid="tab-client-portal">
                <ExternalLink className="w-4 h-4 mr-2" />
                Client Portal
              </TabsTrigger>
              <TabsTrigger value="features" data-testid="tab-features">
                <Play className="w-4 h-4 mr-2" />
                Features
              </TabsTrigger>
              <TabsTrigger value="backups" data-testid="tab-backups">
                <Database className="w-4 h-4 mr-2" />
                Backups
              </TabsTrigger>
              {!isEmployee && (
                <TabsTrigger value="account" data-testid="tab-account">
                  <UserX className="w-4 h-4 mr-2" />
                  Account
                </TabsTrigger>
              )}
            </TabsList>

            {/* Theme Tab */}
            <TabsContent value="theme" data-testid="theme-tab-content">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Palette className="w-6 h-6" />
                    Application Theme
                  </CardTitle>
                  <p className="text-muted-foreground">Choose a color scheme for your dashboard</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {Object.values(themes).map((themeOption) => (
                      <div
                        key={themeOption.id}
                        onClick={() => {
                          setTheme(themeOption.id);
                          toast.success(`Theme changed to ${themeOption.name}`);
                        }}
                        className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                          currentTheme === themeOption.id 
                            ? 'border-neon-blue shadow-lg shadow-neon-blue/20' 
                            : 'border-transparent hover:border-glass-border'
                        }`}
                        data-testid={`theme-option-${themeOption.id}`}
                      >
                        {/* Theme Preview */}
                        <div 
                          className="h-32 p-3 flex flex-col"
                          style={{ backgroundColor: themeOption.preview.bg }}
                        >
                          {/* Preview Header */}
                          <div 
                            className="rounded-lg p-2 mb-2 flex items-center gap-2"
                            style={{ backgroundColor: themeOption.preview.card }}
                          >
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: themeOption.preview.accent }}
                            />
                            <div 
                              className="h-2 w-16 rounded"
                              style={{ backgroundColor: themeOption.preview.text, opacity: 0.3 }}
                            />
                          </div>
                          
                          {/* Preview Content */}
                          <div 
                            className="flex-1 rounded-lg p-2"
                            style={{ backgroundColor: themeOption.preview.card }}
                          >
                            <div 
                              className="h-2 w-full rounded mb-2"
                              style={{ backgroundColor: themeOption.preview.text, opacity: 0.2 }}
                            />
                            <div 
                              className="h-2 w-3/4 rounded mb-2"
                              style={{ backgroundColor: themeOption.preview.text, opacity: 0.15 }}
                            />
                            <div 
                              className="h-4 w-1/2 rounded mt-auto"
                              style={{ backgroundColor: themeOption.preview.accent }}
                            />
                          </div>
                        </div>
                        
                        {/* Theme Label */}
                        <div className="p-3 bg-card">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-semibold text-sm flex items-center gap-2">
                                {themeOption.id === 'light' ? (
                                  <Sun className="w-4 h-4" />
                                ) : (
                                  <Moon className="w-4 h-4" />
                                )}
                                {themeOption.name}
                              </div>
                              <div className="text-xs text-muted-foreground">{themeOption.description}</div>
                            </div>
                            {currentTheme === themeOption.id && (
                              <div className="w-5 h-5 rounded-full bg-neon-blue flex items-center justify-center">
                                <Check className="w-3 h-3 text-black" />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 rounded-xl bg-muted/20 border border-glass-border">
                    <p className="text-sm text-muted-foreground">
                      Your theme preference is saved automatically and will persist across sessions.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Company Info Tab */}
            <TabsContent value="company">
              {/* Maintenance Mode */}
              <Card className={`border-glass-border mb-6 ${settings.maintenance_mode ? 'glass-card border-amber-500/50 bg-amber-500/5' : 'glass-card'}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <AlertCircle className={`w-5 h-5 ${settings.maintenance_mode ? 'text-amber-400' : 'text-muted-foreground'}`} />
                        Client Portal Maintenance Mode
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {settings.maintenance_mode ? 'Client portals are currently showing maintenance message' : 'Toggle to show a maintenance message on all client portals'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${settings.maintenance_mode ? 'text-amber-400' : 'text-muted-foreground'}`}>
                        {settings.maintenance_mode ? 'ON' : 'OFF'}
                      </span>
                      <button
                        data-testid="maintenance-mode-toggle"
                        onClick={() => setSettings({ ...settings, maintenance_mode: !settings.maintenance_mode })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.maintenance_mode ? 'bg-amber-500' : 'bg-white/20'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.maintenance_mode ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </div>
                  {settings.maintenance_mode && (
                    <div className="mt-4 space-y-2">
                      <Label>Message shown to clients</Label>
                      <Input
                        data-testid="maintenance-message-input"
                        value={settings.maintenance_message || ''}
                        onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        placeholder="We are currently performing scheduled maintenance. Please check back shortly."
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Company Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={settings.company_name || ''}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="Your DJ Company Name"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={settings.address || ''}
                      onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="Street address"
                      data-testid="address-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>City</Label>
                      <Input
                        value={settings.city || ''}
                        onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        data-testid="city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input
                        value={settings.state || ''}
                        onChange={(e) => setSettings({ ...settings, state: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        data-testid="state-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>ZIP Code</Label>
                      <Input
                        value={settings.zip_code || ''}
                        onChange={(e) => setSettings({ ...settings, zip_code: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        data-testid="zip-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Country</Label>
                      <Input
                        value={settings.country || ''}
                        onChange={(e) => setSettings({ ...settings, country: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        placeholder="USA"
                        data-testid="country-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={settings.email || ''}
                        onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        placeholder="contact@yourcompany.com"
                        data-testid="company-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telephone</Label>
                      <Input
                        type="tel"
                        value={settings.phone || ''}
                        onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                        className="bg-muted/20 border-glass-border"
                        placeholder="(555) 123-4567"
                        data-testid="company-phone-input"
                      />
                    </div>
                  </div>
                  
                  {/* Business Hours Section */}
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Business Hours</Label>
                    <div className="space-y-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                        const daySettings = settings.business_hours?.[day] || { enabled: false, open: '09:00', close: '17:00' };
                        return (
                          <div 
                            key={day} 
                            className={`flex items-center gap-4 p-3 rounded-xl border transition-colors ${
                              daySettings.enabled ? 'border-primary/50 bg-primary/5' : 'border-glass-border bg-muted/10'
                            }`}
                          >
                            <Switch
                              checked={daySettings.enabled}
                              onCheckedChange={(checked) => {
                                setSettings({
                                  ...settings,
                                  business_hours: {
                                    ...settings.business_hours,
                                    [day]: { ...daySettings, enabled: checked }
                                  }
                                });
                              }}
                              data-testid={`hours-${day}-enabled`}
                            />
                            <span className={`w-24 font-medium capitalize ${daySettings.enabled ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {day}
                            </span>
                            {daySettings.enabled ? (
                              <>
                                <Input
                                  type="time"
                                  value={daySettings.open}
                                  onChange={(e) => {
                                    setSettings({
                                      ...settings,
                                      business_hours: {
                                        ...settings.business_hours,
                                        [day]: { ...daySettings, open: e.target.value }
                                      }
                                    });
                                  }}
                                  className="w-32 bg-muted/20 border-glass-border"
                                  data-testid={`hours-${day}-open`}
                                />
                                <span className="text-muted-foreground">to</span>
                                <Input
                                  type="time"
                                  value={daySettings.close}
                                  onChange={(e) => {
                                    setSettings({
                                      ...settings,
                                      business_hours: {
                                        ...settings.business_hours,
                                        [day]: { ...daySettings, close: e.target.value }
                                      }
                                    });
                                  }}
                                  className="w-32 bg-muted/20 border-glass-border"
                                  data-testid={`hours-${day}-close`}
                                />
                              </>
                            ) : (
                              <span className="text-muted-foreground italic">Closed</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Tax ID</Label>
                    <Input
                      value={settings.tax_id || ''}
                      onChange={(e) => setSettings({ ...settings, tax_id: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="XX-XXXXXXX"
                      data-testid="tax-id-input"
                    />
                  </div>

                  {/* Date/Time Format Settings */}
                  <div className="space-y-4 pt-4 border-t border-glass-border">
                    <Label className="text-lg font-semibold">Date & Time Formatting</Label>
                    <p className="text-sm text-muted-foreground">
                      Choose how dates and times are displayed throughout the application.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Date Format</Label>
                        <Select 
                          value={settings.date_format || 'MM/DD/YYYY'} 
                          onValueChange={(v) => setSettings({ ...settings, date_format: v })}
                        >
                          <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="date-format-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-glass-border">
                            {DATE_FORMATS.map((format) => (
                              <SelectItem key={format.value} value={format.value}>{format.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Time Format</Label>
                        <Select 
                          value={settings.time_format || '12h'} 
                          onValueChange={(v) => setSettings({ ...settings, time_format: v })}
                        >
                          <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="time-format-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-glass-border">
                            {TIME_FORMATS.map((format) => (
                              <SelectItem key={format.value} value={format.value}>{format.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Timezone</Label>
                        <Select 
                          value={settings.timezone || 'America/New_York'} 
                          onValueChange={(v) => setSettings({ ...settings, timezone: v })}
                        >
                          <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="timezone-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-glass-border">
                            {TIMEZONES.map((tz) => (
                              <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Branding Tab */}
            <TabsContent value="branding">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Branding & Logo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Company Logo</Label>
                    <div className="flex items-center gap-6">
                      {logoPreview && (
                        <div className="w-32 h-32 rounded-lg overflow-hidden border border-glass-border bg-white/5 flex items-center justify-center">
                          <img src={logoPreview} alt="Logo" className="max-w-full max-h-full object-contain p-2" />
                        </div>
                      )}
                      <div className="flex-1">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="bg-muted/20 border-glass-border"
                          data-testid="logo-upload-input"
                        />
                        <p className="text-xs text-muted-foreground mt-2">Max size: 1MB. Recommended: Square format (500x500px)</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Brand Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.primary_color || '#06b6d4'}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          className="w-20 h-10 bg-muted/20 border-glass-border cursor-pointer"
                          data-testid="primary-color-input"
                        />
                        <Input
                          value={settings.primary_color || '#06b6d4'}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          className="flex-1 bg-muted/20 border-glass-border font-mono"
                          placeholder="#06b6d4"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Brand Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={settings.secondary_color || '#8b5cf6'}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          className="w-20 h-10 bg-muted/20 border-glass-border cursor-pointer"
                          data-testid="secondary-color-input"
                        />
                        <Input
                          value={settings.secondary_color || '#8b5cf6'}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          className="flex-1 bg-muted/20 border-glass-border font-mono"
                          placeholder="#8b5cf6"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-glass-border">
                    <p className="text-sm text-muted-foreground mb-2">Color Preview:</p>
                    <div className="flex gap-3">
                      <div 
                        className="w-24 h-24 rounded-lg border border-glass-border"
                        style={{ backgroundColor: settings.primary_color }}
                      ></div>
                      <div 
                        className="w-24 h-24 rounded-lg border border-glass-border"
                        style={{ backgroundColor: settings.secondary_color }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Phone Number</Label>
                    <Input
                      value={settings.phone || ''}
                      onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="+1 (555) 123-4567"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={settings.email || ''}
                      onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="contact@yourcompany.com"
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={settings.website || ''}
                      onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="https://yourcompany.com"
                      data-testid="website-input"
                    />
                  </div>

                  {/* Custom Domain */}
                  <div className="space-y-3 pt-4 border-t border-glass-border">
                    <div className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-primary" />
                      <Label className="text-base font-semibold">Custom Domain</Label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Use your own domain for client portal links, emails, and public pages (e.g., <span className="font-mono text-primary">crm.yourdomain.com</span>)
                    </p>
                    <Input
                      value={settings.custom_domain || ''}
                      onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value.replace(/^https?:\/\//, '').replace(/\/$/, '') })}
                      className="bg-muted/20 border-glass-border font-mono"
                      placeholder="crm.yourdomain.com"
                      data-testid="custom-domain-input"
                    />
                    {settings.custom_domain && (
                      <div className="bg-muted/10 border border-glass-border rounded-lg p-4 space-y-3">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500" />
                          DNS Setup Instructions
                        </p>
                        <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                          <li>Go to your DNS provider (GoDaddy, Cloudflare, Namecheap, etc.)</li>
                          <li>
                            Add a <span className="font-mono bg-muted/20 px-1 rounded">CNAME</span> record:
                            <div className="mt-1 ml-4 font-mono text-xs bg-black/20 rounded p-2">
                              <span className="text-primary">{settings.custom_domain.split('.')[0]}</span> → <span className="text-green-400">{window.location.hostname}</span>
                            </div>
                          </li>
                          <li>Wait for DNS propagation (usually 5-30 minutes)</li>
                          <li>Save settings and verify your domain is working</li>
                        </ol>
                        <div className="flex gap-2 pt-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const resp = await axios.post(`${API}/settings/verify-domain`, 
                                  { domain: settings.custom_domain },
                                  { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                                );
                                if (resp.data.status === 'active') {
                                  toast.success('Domain is active and working!');
                                } else if (resp.data.status === 'reachable') {
                                  toast.success('Domain is reachable! It may take a moment to fully propagate.');
                                } else {
                                  toast.warning('Domain not yet reachable. Please check your DNS settings and try again in a few minutes.');
                                }
                              } catch (err) {
                                toast.error('Failed to verify domain');
                              }
                            }}
                            className="text-xs"
                            data-testid="verify-domain-btn"
                          >
                            <Check className="w-3 h-3 mr-1" /> Verify Domain
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              window.open(`https://${settings.custom_domain}`, '_blank');
                            }}
                            className="text-xs"
                            data-testid="test-domain-btn"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" /> Test in Browser
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Social Media Tab */}
            <TabsContent value="social">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Social Media Links</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Facebook URL</Label>
                    <Input
                      value={settings.facebook_url || ''}
                      onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="https://facebook.com/yourpage"
                      data-testid="facebook-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Instagram URL</Label>
                    <Input
                      value={settings.instagram_url || ''}
                      onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="https://instagram.com/yourhandle"
                      data-testid="instagram-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Twitter/X URL</Label>
                    <Input
                      value={settings.twitter_url || ''}
                      onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="https://twitter.com/yourhandle"
                      data-testid="twitter-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>YouTube URL</Label>
                    <Input
                      value={settings.youtube_url || ''}
                      onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })}
                      className="bg-muted/20 border-glass-border"
                      placeholder="https://youtube.com/@yourchannel"
                      data-testid="youtube-input"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payment Tab */}
            <TabsContent value="payment">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Payment Processor</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-muted-foreground">Select your preferred payment processor for accepting client payments. Only one processor can be active at a time.</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { id: 'stripe', name: 'Stripe', description: 'Accept cards, Apple Pay, Google Pay' },
                      { id: 'square', name: 'Square', description: 'In-person and online payments' },
                      { id: 'paypal', name: 'PayPal', description: 'PayPal and card payments' },
                      { id: 'venmo', name: 'Venmo', description: 'Peer-to-peer payments (via PayPal)' }
                    ].map((processor) => (
                      <div
                        key={processor.id}
                        onClick={() => setSettings({ ...settings, payment_processor: processor.id })}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          settings.payment_processor === processor.id
                            ? 'border-neon-blue bg-neon-blue/10'
                            : 'border-glass-border hover:border-glass-border/80 bg-white/5'
                        }`}
                        data-testid={`payment-processor-${processor.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            settings.payment_processor === processor.id
                              ? 'border-neon-blue'
                              : 'border-muted-foreground'
                          }`}>
                            {settings.payment_processor === processor.id && (
                              <div className="w-2.5 h-2.5 rounded-full bg-neon-blue"></div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{processor.name}</h3>
                            <p className="text-sm text-muted-foreground">{processor.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Stripe Configuration */}
                  {settings.payment_processor === 'stripe' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-neon-blue" />
                        Stripe Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your API keys from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">Stripe Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Publishable Key</Label>
                          <Input
                            value={settings.stripe_publishable_key || ''}
                            onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="pk_test_..."
                            data-testid="stripe-publishable-key"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Secret Key</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.stripe_secret ? 'text' : 'password'}
                              value={settings.stripe_secret_key || ''}
                              onChange={(e) => setSettings({ ...settings, stripe_secret_key: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="sk_test_..."
                              data-testid="stripe-secret-key"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, stripe_secret: !showSecrets.stripe_secret })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.stripe_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Webhook Secret (Optional)</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.stripe_webhook ? 'text' : 'password'}
                              value={settings.stripe_webhook_secret || ''}
                              onChange={(e) => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="whsec_..."
                              data-testid="stripe-webhook-secret"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, stripe_webhook: !showSecrets.stripe_webhook })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.stripe_webhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Square Configuration */}
                  {settings.payment_processor === 'square' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-neon-blue" />
                        Square Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your credentials from the <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">Square Developer Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Environment</Label>
                          <Select 
                            value={settings.square_environment || 'sandbox'} 
                            onValueChange={(v) => setSettings({ ...settings, square_environment: v })}
                          >
                            <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="square-environment">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-obsidian border-glass-border">
                              <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                              <SelectItem value="production">Production (Live)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Application ID</Label>
                          <Input
                            value={settings.square_application_id || ''}
                            onChange={(e) => setSettings({ ...settings, square_application_id: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="sq0idp-..."
                            data-testid="square-application-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Access Token</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.square_token ? 'text' : 'password'}
                              value={settings.square_access_token || ''}
                              onChange={(e) => setSettings({ ...settings, square_access_token: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="EAAAl..."
                              data-testid="square-access-token"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, square_token: !showSecrets.square_token })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.square_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Location ID</Label>
                          <Input
                            value={settings.square_location_id || ''}
                            onChange={(e) => setSettings({ ...settings, square_location_id: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="L..."
                            data-testid="square-location-id"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* PayPal Configuration */}
                  {settings.payment_processor === 'paypal' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-neon-blue" />
                        PayPal Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your credentials from the <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">PayPal Developer Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Environment</Label>
                          <Select 
                            value={settings.paypal_environment || 'sandbox'} 
                            onValueChange={(v) => setSettings({ ...settings, paypal_environment: v })}
                          >
                            <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="paypal-environment">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-obsidian border-glass-border">
                              <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                              <SelectItem value="production">Production (Live)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Client ID</Label>
                          <Input
                            value={settings.paypal_client_id || ''}
                            onChange={(e) => setSettings({ ...settings, paypal_client_id: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="AV..."
                            data-testid="paypal-client-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Client Secret</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.paypal_secret ? 'text' : 'password'}
                              value={settings.paypal_client_secret || ''}
                              onChange={(e) => setSettings({ ...settings, paypal_client_secret: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="EL..."
                              data-testid="paypal-client-secret"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, paypal_secret: !showSecrets.paypal_secret })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.paypal_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Venmo Configuration */}
                  {settings.payment_processor === 'venmo' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-neon-blue" />
                        Venmo Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Venmo payments are processed through PayPal. Get your credentials from the <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">PayPal Developer Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Environment</Label>
                          <Select 
                            value={settings.venmo_environment || 'sandbox'} 
                            onValueChange={(v) => setSettings({ ...settings, venmo_environment: v })}
                          >
                            <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="venmo-environment">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-obsidian border-glass-border">
                              <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                              <SelectItem value="production">Production (Live)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Client ID</Label>
                          <Input
                            value={settings.venmo_client_id || ''}
                            onChange={(e) => setSettings({ ...settings, venmo_client_id: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="AV..."
                            data-testid="venmo-client-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Client Secret</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.venmo_secret ? 'text' : 'password'}
                              value={settings.venmo_client_secret || ''}
                              onChange={(e) => setSettings({ ...settings, venmo_client_secret: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="EL..."
                              data-testid="venmo-client-secret"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, venmo_secret: !showSecrets.venmo_secret })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.venmo_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {!settings.payment_processor && (
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-sm text-yellow-500">
                        No payment processor selected. Please select one to enable payment collection.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Email Provider</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-muted-foreground">Configure your email provider to send contracts, invoices, and notifications to clients.</p>
                  
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'sendgrid', name: 'SendGrid', description: 'Reliable email delivery API' },
                      { id: 'resend', name: 'Resend', description: 'Modern email for developers' },
                      { id: 'smtp', name: 'SMTP', description: 'Gmail, Outlook, custom server' }
                    ].map((provider) => (
                      <div
                        key={provider.id}
                        onClick={() => setSettings({ ...settings, email_provider: provider.id })}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          settings.email_provider === provider.id
                            ? 'border-neon-blue bg-neon-blue/10'
                            : 'border-glass-border hover:border-glass-border/80 bg-white/5'
                        }`}
                        data-testid={`email-provider-${provider.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            settings.email_provider === provider.id
                              ? 'border-neon-blue'
                              : 'border-muted-foreground'
                          }`}>
                            {settings.email_provider === provider.id && (
                              <div className="w-2.5 h-2.5 rounded-full bg-neon-blue"></div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold">{provider.name}</h3>
                            <p className="text-sm text-muted-foreground">{provider.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* SendGrid Configuration */}
                  {settings.email_provider === 'sendgrid' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5 text-neon-blue" />
                        SendGrid Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your API key from the <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">SendGrid Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>API Key</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.sendgrid_key ? 'text' : 'password'}
                              value={settings.sendgrid_api_key || ''}
                              onChange={(e) => setSettings({ ...settings, sendgrid_api_key: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="SG.xxx..."
                              data-testid="sendgrid-api-key"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, sendgrid_key: !showSecrets.sendgrid_key })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.sendgrid_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>From Email</Label>
                            <Input
                              type="email"
                              value={settings.sendgrid_from_email || ''}
                              onChange={(e) => setSettings({ ...settings, sendgrid_from_email: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="noreply@yourdomain.com"
                              data-testid="sendgrid-from-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>From Name</Label>
                            <Input
                              value={settings.sendgrid_from_name || ''}
                              onChange={(e) => setSettings({ ...settings, sendgrid_from_name: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="Your Company Name"
                              data-testid="sendgrid-from-name"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resend Configuration */}
                  {settings.email_provider === 'resend' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5 text-neon-blue" />
                        Resend Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your API key from the <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">Resend Dashboard</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>API Key</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.resend_key ? 'text' : 'password'}
                              value={settings.resend_api_key || ''}
                              onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="re_xxx..."
                              data-testid="resend-api-key"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, resend_key: !showSecrets.resend_key })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.resend_key ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>From Email</Label>
                            <Input
                              type="email"
                              value={settings.resend_from_email || ''}
                              onChange={(e) => setSettings({ ...settings, resend_from_email: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="noreply@yourdomain.com"
                              data-testid="resend-from-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>From Name</Label>
                            <Input
                              value={settings.resend_from_name || ''}
                              onChange={(e) => setSettings({ ...settings, resend_from_name: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="Your Company Name"
                              data-testid="resend-from-name"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* SMTP Configuration */}
                  {settings.email_provider === 'smtp' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Mail className="w-5 h-5 text-neon-blue" />
                        SMTP Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Configure your own SMTP server or use Gmail/Outlook SMTP.
                      </p>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>SMTP Host</Label>
                            <Input
                              value={settings.smtp_host || ''}
                              onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="smtp.gmail.com"
                              data-testid="smtp-host"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>SMTP Port</Label>
                            <Input
                              type="number"
                              value={settings.smtp_port || 587}
                              onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="587"
                              data-testid="smtp-port"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Username</Label>
                            <Input
                              value={settings.smtp_username || ''}
                              onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="your-email@gmail.com"
                              data-testid="smtp-username"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Password / App Password</Label>
                            <div className="relative">
                              <Input
                                type={showSecrets.smtp_password ? 'text' : 'password'}
                                value={settings.smtp_password || ''}
                                onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                                className="bg-muted/20 border-glass-border pr-10"
                                placeholder="••••••••"
                                data-testid="smtp-password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSecrets({ ...showSecrets, smtp_password: !showSecrets.smtp_password })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showSecrets.smtp_password ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>From Email</Label>
                            <Input
                              type="email"
                              value={settings.smtp_from_email || ''}
                              onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="noreply@yourdomain.com"
                              data-testid="smtp-from-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>From Name</Label>
                            <Input
                              value={settings.smtp_from_name || ''}
                              onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                              className="bg-muted/20 border-glass-border"
                              placeholder="Your Company Name"
                              data-testid="smtp-from-name"
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            id="smtp-tls"
                            checked={settings.smtp_use_tls !== false}
                            onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked })}
                            data-testid="smtp-use-tls"
                          />
                          <Label htmlFor="smtp-tls">Use TLS/SSL encryption</Label>
                        </div>
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                          <p className="text-sm text-blue-400">
                            <strong>Gmail users:</strong> Use an App Password instead of your regular password. 
                            <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline ml-1">Generate App Password</a>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Test Email Section */}
                  {settings.email_provider && (
                    <div className="mt-6 p-4 rounded-lg bg-muted/20 border border-glass-border">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Send Test Email
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Verify your email configuration is working correctly by sending a test email.
                      </p>
                      <div className="flex gap-3">
                        <Input
                          type="email"
                          value={testEmailAddress}
                          onChange={(e) => setTestEmailAddress(e.target.value)}
                          placeholder="your@email.com"
                          className="bg-muted/20 border-glass-border flex-1"
                          data-testid="test-email-address"
                        />
                        <Button
                          onClick={sendTestEmail}
                          disabled={sendingTestEmail || !testEmailAddress}
                          className="bg-neon-blue text-white hover:bg-neon-blue/80"
                          data-testid="send-test-email-btn"
                        >
                          {sendingTestEmail ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {!settings.email_provider && (
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-sm text-yellow-500">
                        No email provider selected. Configure one to send contracts and invoices to clients.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SMS Tab */}
            <TabsContent value="sms">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">SMS / Text Messaging</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-muted-foreground">Configure an SMS provider to send text reminders and notifications to clients.</p>
                  
                  {/* Provider Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                    {/* Twilio Option */}
                    <div
                      onClick={() => setSettings({ ...settings, sms_provider: settings.sms_provider === 'twilio' ? '' : 'twilio' })}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        settings.sms_provider === 'twilio'
                          ? 'border-neon-blue bg-neon-blue/10'
                          : 'border-glass-border hover:border-glass-border/80 bg-white/5'
                      }`}
                      data-testid="sms-provider-twilio"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          settings.sms_provider === 'twilio'
                            ? 'border-neon-blue'
                            : 'border-muted-foreground'
                        }`}>
                          {settings.sms_provider === 'twilio' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-neon-blue"></div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">Twilio</h3>
                          <p className="text-sm text-muted-foreground">SMS, MMS, and WhatsApp messaging</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Vonage Option */}
                    <div
                      onClick={() => setSettings({ ...settings, sms_provider: settings.sms_provider === 'vonage' ? '' : 'vonage' })}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        settings.sms_provider === 'vonage'
                          ? 'border-green-500 bg-green-500/10'
                          : 'border-glass-border hover:border-glass-border/80 bg-white/5'
                      }`}
                      data-testid="sms-provider-vonage"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          settings.sms_provider === 'vonage'
                            ? 'border-green-500'
                            : 'border-muted-foreground'
                        }`}>
                          {settings.sms_provider === 'vonage' && (
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                          )}
                        </div>
                        <div>
                          <h3 className="font-semibold">Vonage</h3>
                          <p className="text-sm text-muted-foreground">Global SMS messaging platform</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Twilio Configuration */}
                  {settings.sms_provider === 'twilio' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-neon-blue" />
                        Twilio Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your credentials from the <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">Twilio Console</a>
                      </p>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Account SID</Label>
                          <Input
                            value={settings.twilio_account_sid || ''}
                            onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="ACxxx..."
                            data-testid="twilio-account-sid"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Auth Token</Label>
                          <div className="relative">
                            <Input
                              type={showSecrets.twilio_token ? 'text' : 'password'}
                              value={settings.twilio_auth_token || ''}
                              onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                              placeholder="••••••••••••••••"
                              data-testid="twilio-auth-token"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSecrets({ ...showSecrets, twilio_token: !showSecrets.twilio_token })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              {showSecrets.twilio_token ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Twilio Phone Number</Label>
                          <Input
                            value={settings.twilio_phone_number || ''}
                            onChange={(e) => setSettings({ ...settings, twilio_phone_number: e.target.value })}
                            className="bg-muted/20 border-glass-border"
                            placeholder="+1234567890"
                            data-testid="twilio-phone-number"
                          />
                          <p className="text-xs text-muted-foreground">Your Twilio phone number in E.164 format (e.g., +1234567890)</p>
                        </div>
                        
                        {/* Messaging Service SID - Recommended */}
                        <div className="space-y-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                          <Label className="flex items-center gap-2">
                            Messaging Service SID 
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Recommended</span>
                          </Label>
                          <Input
                            value={settings.twilio_messaging_service_sid || ''}
                            onChange={(e) => setSettings({ ...settings, twilio_messaging_service_sid: e.target.value })}
                            className="bg-muted/20 border-glass-border font-mono text-sm"
                            placeholder="MGxxx..."
                            data-testid="twilio-messaging-service-sid"
                          />
                          <p className="text-xs text-muted-foreground">
                            Using a Messaging Service improves deliverability and handles A2P 10DLC compliance automatically.
                            Create one in <a href="https://console.twilio.com/us1/develop/sms/services" target="_blank" rel="noopener noreferrer" className="text-neon-blue hover:underline">Twilio Console → Messaging → Services</a>
                          </p>
                        </div>
                      </div>
                      
                      {/* Send Test SMS - Twilio */}
                      <div className="border-t border-glass-border pt-4 mt-4">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Send className="w-4 h-4 text-neon-blue" />
                          Test SMS Configuration
                        </h4>
                        <div className="flex gap-2">
                          <Input
                            value={testSmsPhone}
                            onChange={(e) => setTestSmsPhone(e.target.value)}
                            placeholder="Enter phone number to test (+1234567890)"
                            className="bg-muted/20 border-glass-border flex-1"
                            data-testid="test-sms-phone-input"
                          />
                          <Button
                            onClick={sendTestSms}
                            disabled={sendingTestSms || !testSmsPhone}
                            className="bg-neon-blue hover:bg-neon-blue/80"
                            data-testid="send-test-sms-btn"
                          >
                            {sendingTestSms ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Test
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Send a test SMS to verify your Twilio configuration is working correctly.
                        </p>
                        <p className="text-xs text-yellow-500/80 mt-1">
                          <strong>Note:</strong> If using a Twilio trial account, you can only send SMS to verified phone numbers. 
                          For production use with US numbers, A2P 10DLC registration may be required.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Vonage Configuration */}
                  {settings.sms_provider === 'vonage' && (
                    <div className="space-y-4 p-4 rounded-lg bg-white/5 border border-glass-border">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-green-500" />
                        Vonage Configuration
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Get your credentials from the <a href="https://dashboard.nexmo.com" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">Vonage Dashboard</a>
                      </p>
                      
                      {/* Messages API (Recommended) */}
                      <div className="space-y-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                        <Label className="flex items-center gap-2">
                          Messages API Configuration
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Recommended for 10DLC</span>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Create an application in <a href="https://dashboard.nexmo.com/applications" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">Vonage Dashboard → Applications</a> with Messages capability enabled.
                        </p>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label>Application ID</Label>
                            <Input
                              value={settings.vonage_application_id || ''}
                              onChange={(e) => setSettings({ ...settings, vonage_application_id: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm"
                              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                              data-testid="vonage-application-id"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Private Key</Label>
                            <textarea
                              value={settings.vonage_private_key || ''}
                              onChange={(e) => setSettings({ ...settings, vonage_private_key: e.target.value })}
                              className="w-full h-32 bg-muted/20 border border-glass-border rounded-lg p-3 font-mono text-xs resize-none"
                              placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                              data-testid="vonage-private-key"
                            />
                            <p className="text-xs text-muted-foreground">
                              Paste the entire contents of the private.key file downloaded when creating the application.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* SMS API (Alternative) */}
                      <div className="space-y-4 p-3 rounded-lg bg-muted/20 border border-glass-border">
                        <Label className="flex items-center gap-2 text-muted-foreground">
                          SMS API (Alternative)
                          <span className="text-xs bg-muted/40 px-2 py-0.5 rounded">Basic</span>
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Only use if you don't have an application set up. Get these from <a href="https://dashboard.nexmo.com/settings" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">API Settings</a>
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label className="text-sm">API Key</Label>
                            <Input
                              value={settings.vonage_api_key || ''}
                              onChange={(e) => setSettings({ ...settings, vonage_api_key: e.target.value })}
                              className="bg-muted/20 border-glass-border font-mono text-sm"
                              placeholder="abc123..."
                              data-testid="vonage-api-key"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm">API Secret</Label>
                            <div className="relative">
                              <Input
                                type={showSecrets.vonage_secret ? 'text' : 'password'}
                                value={settings.vonage_api_secret || ''}
                                onChange={(e) => setSettings({ ...settings, vonage_api_secret: e.target.value })}
                                className="bg-muted/20 border-glass-border font-mono text-sm pr-10"
                                placeholder="••••••••"
                                data-testid="vonage-api-secret"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSecrets({ ...showSecrets, vonage_secret: !showSecrets.vonage_secret })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              >
                                {showSecrets.vonage_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label>Vonage Phone Number</Label>
                        <Input
                          value={settings.vonage_phone_number || ''}
                          onChange={(e) => setSettings({ ...settings, vonage_phone_number: e.target.value })}
                          className="bg-muted/20 border-glass-border"
                          placeholder="16032386603"
                          data-testid="vonage-phone-number"
                        />
                        <p className="text-xs text-muted-foreground">
                          Your Vonage virtual number (without +). Make sure this number is linked to your application in the Vonage Dashboard.
                        </p>
                      </div>
                      
                      {/* Send Test SMS - Vonage */}
                      <div className="border-t border-glass-border pt-4 mt-4">
                        <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                          <Send className="w-4 h-4 text-green-500" />
                          Test SMS Configuration
                        </h4>
                        <div className="flex gap-2">
                          <Input
                            value={testSmsPhone}
                            onChange={(e) => setTestSmsPhone(e.target.value)}
                            placeholder="Enter phone number to test (+1234567890)"
                            className="bg-muted/20 border-glass-border flex-1"
                            data-testid="test-sms-phone-input-vonage"
                          />
                          <Button
                            onClick={sendTestSms}
                            disabled={sendingTestSms || !testSmsPhone}
                            className="bg-green-500 hover:bg-green-500/80"
                            data-testid="send-test-sms-btn-vonage"
                          >
                            {sendingTestSms ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Test
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          Send a test SMS to verify your Vonage configuration is working correctly.
                        </p>
                      </div>
                    </div>
                  )}

                  {!settings.sms_provider && (
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                      <p className="text-sm text-yellow-500">
                        No SMS provider configured. Select Twilio or Vonage above to send text message reminders to clients.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Spotify Tab */}
            <TabsContent value="spotify">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Music className="w-6 h-6" />
                    Spotify Integration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <h4 className="font-semibold text-green-400 mb-2">Import Playlists from Spotify</h4>
                    <p className="text-sm text-muted-foreground">
                      Connect your Spotify Developer account to import public playlists directly into your DJ CRM music playlists.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Spotify Client ID</Label>
                      <Input
                        value={settings.spotify_client_id || ''}
                        onChange={(e) => setSettings({ ...settings, spotify_client_id: e.target.value })}
                        placeholder="Enter your Spotify Client ID"
                        className="bg-background border-border text-foreground"
                        data-testid="spotify-client-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Spotify Client Secret</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets['spotify_client_secret'] ? 'text' : 'password'}
                          value={settings.spotify_client_secret || ''}
                          onChange={(e) => setSettings({ ...settings, spotify_client_secret: e.target.value })}
                          placeholder="Enter your Spotify Client Secret"
                          className="bg-background border-border text-foreground pr-10"
                          data-testid="spotify-client-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecrets({ ...showSecrets, spotify_client_secret: !showSecrets['spotify_client_secret'] })}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showSecrets['spotify_client_secret'] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-foreground">How to get Spotify API credentials:</h4>
                    <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                      <li>Go to <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">Spotify Developer Dashboard</a></li>
                      <li>Log in with your Spotify account</li>
                      <li>Click "Create App"</li>
                      <li>Fill in the app name (e.g., "DJ CRM") and description</li>
                      <li>Set the Redirect URI to: <code className="bg-black/30 px-2 py-0.5 rounded select-all">{window.location.origin}/callback</code></li>
                      <li>Check "Web API" under the APIs section</li>
                      <li>Copy the Client ID and Client Secret from your app's settings</li>
                    </ol>
                  </div>

                  <div className="text-sm text-muted-foreground">
                    <div className="mt-4 p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
                      <h4 className="font-semibold text-amber-400 mb-2">Important: Extended Quota Mode Required</h4>
                      <p className="text-sm text-muted-foreground">
                        Spotify requires apps to have <strong>"Extended Quota Mode"</strong> approval to access playlist data. 
                        After creating your app in the Spotify Developer Dashboard:
                      </p>
                      <ol className="list-decimal list-inside mt-2 text-sm text-muted-foreground space-y-1">
                        <li>Go to your app in the <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">Spotify Developer Dashboard</a></li>
                        <li>Click on <strong>"Request Extension"</strong> or <strong>"Request Quota Extension"</strong></li>
                        <li>Fill out the form explaining your use case (DJ music management)</li>
                        <li>Wait for Spotify's approval (usually 1-3 business days)</li>
                      </ol>
                      <p className="text-sm text-muted-foreground mt-2">
                        Without Extended Quota Mode, you'll get a "403 Forbidden" error when trying to import playlists.
                      </p>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mt-4"><strong>Note:</strong> With these credentials and Extended Quota Mode, you can import any <strong>public</strong> Spotify playlist. The playlist URL or link can be found by clicking "Share" on any Spotify playlist.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Payment Types Tab */}
            <TabsContent value="payment-types">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <DollarSign className="w-6 h-6" />
                    Payment Types
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
                    <h4 className="font-semibold text-primary mb-2">Configure Payment Methods</h4>
                    <p className="text-sm text-muted-foreground">
                      Add or remove payment types that appear in the dropdown when recording payments for bookings.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        value={newPaymentType}
                        onChange={(e) => setNewPaymentType(e.target.value)}
                        placeholder="Add new payment type..."
                        className="bg-background border-border text-foreground"
                        onKeyPress={(e) => e.key === 'Enter' && addPaymentType()}
                        data-testid="new-payment-type-input"
                      />
                      <Button onClick={addPaymentType} className="bg-primary text-primary-foreground hover:bg-primary/80">
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground">Current Payment Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {(settings.payment_types || []).map((type, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 bg-muted/30 border border-border rounded-lg px-3 py-2"
                            data-testid={`payment-type-${index}`}
                          >
                            <span className="text-foreground">{type}</span>
                            <button
                              onClick={() => removePaymentType(type)}
                              className="text-muted-foreground hover:text-red-500 transition-colors"
                              data-testid={`remove-payment-type-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      {(!settings.payment_types || settings.payment_types.length === 0) && (
                        <p className="text-sm text-muted-foreground">No payment types configured. Add some above.</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-muted/30 rounded-xl p-4">
                    <h4 className="font-semibold text-foreground mb-2">Common Payment Types</h4>
                    <p className="text-sm text-muted-foreground mb-3">Click to add commonly used payment types:</p>
                    <div className="flex flex-wrap gap-2">
                      {['Cash', 'Check', 'Credit Card', 'Debit Card', 'Venmo', 'PayPal', 'Zelle', 'Bank Transfer', 'Wire Transfer', 'Square', 'Stripe'].map((type) => (
                        <Button
                          key={type}
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (!settings.payment_types?.includes(type)) {
                              setSettings({
                                ...settings,
                                payment_types: [...(settings.payment_types || []), type]
                              });
                            } else {
                              toast.info(`"${type}" already exists`);
                            }
                          }}
                          className="border-border text-foreground hover:bg-primary/10"
                          disabled={settings.payment_types?.includes(type)}
                        >
                          {type}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Bell className="w-6 h-6" />
                    Notification Settings
                  </CardTitle>
                  <p className="text-muted-foreground">Configure how you want to be notified about important events.</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {[
                    { key: 'notify_new_lead', label: 'New Lead', description: 'When a new lead is submitted through your website or forms' },
                    { key: 'notify_client_login', label: 'Client Login', description: 'When a client logs into their portal' },
                    { key: 'notify_invoice_payment', label: 'Invoice Payment', description: 'When a client makes a payment on an invoice' },
                    { key: 'notify_payment_received', label: 'Payment Received', description: 'When any payment is recorded for a booking' },
                    { key: 'notify_contract_signed', label: 'Contract Signed', description: 'When a client signs their contract' },
                    { key: 'notify_form_edited', label: 'Form Edited', description: 'When a client submits or updates a planning form' }
                  ].map((notification) => (
                    <div 
                      key={notification.key}
                      className="flex items-center justify-between p-4 rounded-xl border border-glass-border bg-muted/10"
                    >
                      <div className="space-y-1">
                        <h4 className="font-medium text-foreground">{notification.label}</h4>
                        <p className="text-sm text-muted-foreground">{notification.description}</p>
                      </div>
                      <Select
                        value={settings[notification.key] || 'none'}
                        onValueChange={(value) => setSettings({ ...settings, [notification.key]: value })}
                      >
                        <SelectTrigger className="w-40 bg-muted/20 border-glass-border" data-testid={`${notification.key}-select`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            <span className="flex items-center gap-2">
                              <X className="w-4 h-4 text-muted-foreground" />
                              None
                            </span>
                          </SelectItem>
                          <SelectItem value="email">
                            <span className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-blue-500" />
                              Email
                            </span>
                          </SelectItem>
                          <SelectItem value="sms">
                            <span className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-green-500" />
                              SMS
                            </span>
                          </SelectItem>
                          <SelectItem value="both">
                            <span className="flex items-center gap-2">
                              <Bell className="w-4 h-4 text-purple-500" />
                              Both
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}

                  <div className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/10">
                    <p className="text-sm text-amber-200">
                      <strong>Note:</strong> Email and SMS notifications require you to configure your Email and SMS/Text settings in the respective tabs.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Key className="w-6 h-6" />
                    Security Settings
                  </CardTitle>
                  <p className="text-muted-foreground">Manage your account security and password.</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 rounded-xl border border-glass-border bg-muted/10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Lock className="w-6 h-6 text-amber-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Change Password</h3>
                        <p className="text-muted-foreground text-sm">Update your account password</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4 max-w-md">
                      <div className="space-y-2">
                        <Label>Current Password</Label>
                        <div className="relative">
                          <Input
                            type={showPasswordFields.current ? 'text' : 'password'}
                            value={passwordForm.current}
                            onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                            placeholder="Enter current password"
                            className="bg-muted/20 border-glass-border pr-10"
                            data-testid="settings-current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswordFields({ ...showPasswordFields, current: !showPasswordFields.current })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPasswordFields.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>New Password</Label>
                        <div className="relative">
                          <Input
                            type={showPasswordFields.new ? 'text' : 'password'}
                            value={passwordForm.new}
                            onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                            placeholder="Enter new password (min. 6 characters)"
                            className="bg-muted/20 border-glass-border pr-10"
                            data-testid="settings-new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswordFields({ ...showPasswordFields, new: !showPasswordFields.new })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPasswordFields.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Confirm New Password</Label>
                        <div className="relative">
                          <Input
                            type={showPasswordFields.confirm ? 'text' : 'password'}
                            value={passwordForm.confirm}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                            placeholder="Re-enter new password"
                            className="bg-muted/20 border-glass-border pr-10"
                            data-testid="settings-confirm-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswordFields({ ...showPasswordFields, confirm: !showPasswordFields.confirm })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPasswordFields.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {passwordForm.new && passwordForm.confirm && passwordForm.new !== passwordForm.confirm && (
                          <p className="text-red-400 text-sm">Passwords do not match</p>
                        )}
                      </div>
                      
                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm || passwordForm.new !== passwordForm.confirm}
                        className="bg-neon-blue hover:bg-neon-blue/80 text-black font-semibold"
                        data-testid="submit-password-change"
                      >
                        {changingPassword ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Updating...
                          </>
                        ) : (
                          'Update Password'
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="p-4 rounded-xl border border-blue-500/30 bg-blue-500/10">
                    <p className="text-sm text-blue-200">
                      <strong>Security Tip:</strong> Use a strong password with at least 6 characters including a mix of letters, numbers, and symbols. Never share your password with anyone.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Webhooks / Zapier Tab */}
            <TabsContent value="webhooks">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Link2 className="w-6 h-6" />
                    Webhooks / Zapier Integration
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Connect your DJ CRM to Zapier and other automation tools. Get notified when events happen.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Webhooks Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                    <div>
                      <h3 className="font-medium">Enable Webhooks</h3>
                      <p className="text-sm text-muted-foreground">Turn on webhook notifications for all configured events</p>
                    </div>
                    <Switch
                      checked={settings.webhook_enabled}
                      onCheckedChange={(checked) => setSettings({...settings, webhook_enabled: checked})}
                      data-testid="webhook-enabled-toggle"
                    />
                  </div>

                  {settings.webhook_enabled && (
                    <>
                      {/* Zapier Instructions */}
                      <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                        <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          How to use with Zapier
                        </h4>
                        <ol className="text-sm text-blue-200 space-y-1 list-decimal list-inside">
                          <li>Go to <a href="https://zapier.com" target="_blank" rel="noopener noreferrer" className="underline">Zapier.com</a> and create a new Zap</li>
                          <li>Choose "Webhooks by Zapier" as your trigger</li>
                          <li>Select "Catch Hook" as the event</li>
                          <li>Copy the webhook URL Zapier gives you</li>
                          <li>Paste it in the appropriate field below</li>
                          <li>Click "Test" to verify it works</li>
                        </ol>
                      </div>

                      {/* Webhook URLs */}
                      <div className="space-y-4">
                        {/* Booking Created */}
                        <div className="space-y-2">
                          <Label>New Booking Created</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a new booking is created</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_booking_created || ''}
                              onChange={(e) => setSettings({...settings, webhook_booking_created: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-booking-created-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('booking_created', settings.webhook_booking_created)}
                              disabled={testingWebhook === 'booking_created'}
                              data-testid="test-webhook-booking-created"
                            >
                              {testingWebhook === 'booking_created' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Contract Signed */}
                        <div className="space-y-2">
                          <Label>Contract Signed</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a client signs a contract</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_contract_signed || ''}
                              onChange={(e) => setSettings({...settings, webhook_contract_signed: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-contract-signed-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('contract_signed', settings.webhook_contract_signed)}
                              disabled={testingWebhook === 'contract_signed'}
                            >
                              {testingWebhook === 'contract_signed' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Payment Received */}
                        <div className="space-y-2">
                          <Label>Payment Received</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a payment is recorded</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_payment_received || ''}
                              onChange={(e) => setSettings({...settings, webhook_payment_received: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-payment-received-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('payment_received', settings.webhook_payment_received)}
                              disabled={testingWebhook === 'payment_received'}
                            >
                              {testingWebhook === 'payment_received' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Form Submitted */}
                        <div className="space-y-2">
                          <Label>Form Submitted</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a client submits or updates a form</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_form_submitted || ''}
                              onChange={(e) => setSettings({...settings, webhook_form_submitted: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-form-submitted-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('form_submitted', settings.webhook_form_submitted)}
                              disabled={testingWebhook === 'form_submitted'}
                            >
                              {testingWebhook === 'form_submitted' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Guest Song Request */}
                        <div className="space-y-2">
                          <Label>Guest Song Request</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a guest submits a song request</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_guest_request || ''}
                              onChange={(e) => setSettings({...settings, webhook_guest_request: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-guest-request-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('guest_request', settings.webhook_guest_request)}
                              disabled={testingWebhook === 'guest_request'}
                            >
                              {testingWebhook === 'guest_request' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* New Lead */}
                        <div className="space-y-2">
                          <Label>New Lead Created</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a new lead is added</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_lead_created || ''}
                              onChange={(e) => setSettings({...settings, webhook_lead_created: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-lead-created-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('lead_created', settings.webhook_lead_created)}
                              disabled={testingWebhook === 'lead_created'}
                            >
                              {testingWebhook === 'lead_created' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>

                        {/* Message Received */}
                        <div className="space-y-2">
                          <Label>Client Message Received</Label>
                          <p className="text-xs text-muted-foreground">Triggered when a client sends a message through their portal</p>
                          <div className="flex gap-2">
                            <Input
                              value={settings.webhook_message_received || ''}
                              onChange={(e) => setSettings({...settings, webhook_message_received: e.target.value})}
                              placeholder="https://hooks.zapier.com/..."
                              className="bg-white/10 border-white/20"
                              data-testid="webhook-message-received-input"
                            />
                            <Button
                              variant="outline"
                              onClick={() => testWebhook('message_received', settings.webhook_message_received)}
                              disabled={testingWebhook === 'message_received'}
                            >
                              {testingWebhook === 'message_received' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Save Reminder */}
                      <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm text-yellow-200">
                        <strong>Remember:</strong> Click "Save Settings" at the bottom of the page to save your webhook URLs.
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Meeting Integrations Tab */}
            <TabsContent value="meetings">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Video className="w-6 h-6" />
                    Meeting Integrations
                  </CardTitle>
                  <p className="text-muted-foreground">
                    Configure Zoom, Google Meet, or Microsoft Teams to automatically generate meeting links for virtual appointments.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Provider Selection */}
                  <div className="space-y-2">
                    <Label>Default Meeting Provider</Label>
                    <p className="text-xs text-muted-foreground mb-2">Select which platform to use for generating virtual meeting links</p>
                    <Select
                      value={settings.meeting_provider || 'none'}
                      onValueChange={(value) => setSettings({...settings, meeting_provider: value === 'none' ? '' : value})}
                    >
                      <SelectTrigger className="bg-white/10 border-white/20" data-testid="meeting-provider-select">
                        <SelectValue placeholder="Select a meeting provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="zoom">Zoom</SelectItem>
                        <SelectItem value="google_meet">Google Meet</SelectItem>
                        <SelectItem value="teams">Microsoft Teams</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Auto-generate Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
                    <div>
                      <h3 className="font-medium">Auto-Generate Meeting Links</h3>
                      <p className="text-sm text-muted-foreground">Automatically create meeting links when marking a booking as virtual</p>
                    </div>
                    <Switch
                      checked={settings.auto_generate_meeting_links || false}
                      onCheckedChange={(checked) => setSettings({...settings, auto_generate_meeting_links: checked})}
                      data-testid="auto-generate-meetings-toggle"
                    />
                  </div>

                  {/* Zoom Configuration */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/20">
                          <Video className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-medium">Zoom</h4>
                          <p className="text-xs text-muted-foreground">Server-to-Server OAuth</p>
                        </div>
                      </div>
                      {settings.zoom_account_id && settings.zoom_client_id && settings.zoom_client_secret && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            setTestingMeeting('zoom');
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`${API}/meetings/test-connection/zoom`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (response.data.success) {
                                toast.success('Zoom connection successful!');
                              } else {
                                toast.error(response.data.error || 'Zoom connection failed');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to test Zoom connection');
                            }
                            setTestingMeeting(null);
                          }}
                          disabled={testingMeeting === 'zoom'}
                          data-testid="test-zoom-connection"
                        >
                          {testingMeeting === 'zoom' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                          Test
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Account ID</Label>
                        <Input
                          value={settings.zoom_account_id || ''}
                          onChange={(e) => setSettings({...settings, zoom_account_id: e.target.value})}
                          placeholder="Your Zoom Account ID"
                          className="bg-white/10 border-white/20"
                          data-testid="zoom-account-id-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Client ID</Label>
                        <Input
                          value={settings.zoom_client_id || ''}
                          onChange={(e) => setSettings({...settings, zoom_client_id: e.target.value})}
                          placeholder="Your Zoom Client ID"
                          className="bg-white/10 border-white/20"
                          data-testid="zoom-client-id-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showSecrets.zoom_client_secret ? 'text' : 'password'}
                            value={settings.zoom_client_secret || ''}
                            onChange={(e) => setSettings({...settings, zoom_client_secret: e.target.value})}
                            placeholder="Your Zoom Client Secret"
                            className="bg-white/10 border-white/20 pr-10"
                            data-testid="zoom-client-secret-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecrets({...showSecrets, zoom_client_secret: !showSecrets.zoom_client_secret})}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showSecrets.zoom_client_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Get credentials from the <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noopener noreferrer" className="text-neon-blue underline">Zoom App Marketplace</a>. 
                      Create a Server-to-Server OAuth app with the "OnlineMeetings:write" scope.
                    </p>
                  </div>

                  {/* Google Meet Configuration */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-500/20">
                          <Video className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-medium">Google Meet</h4>
                          <p className="text-xs text-muted-foreground">OAuth 2.0 via Google Calendar</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {settings.google_refresh_token ? (
                          <span className="text-xs text-green-400 flex items-center gap-1">
                            <Check className="w-4 h-4" /> Connected
                          </span>
                        ) : settings.google_client_id && settings.google_client_secret ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.get(`${API}/meetings/google/auth-url`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                window.open(response.data.auth_url, '_blank');
                              } catch (error) {
                                toast.error('Failed to get Google auth URL');
                              }
                            }}
                            data-testid="connect-google-btn"
                          >
                            Connect Google
                          </Button>
                        ) : null}
                        {settings.google_refresh_token && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              setTestingMeeting('google');
                              try {
                                const token = localStorage.getItem('token');
                                const response = await axios.get(`${API}/meetings/test-connection/google_meet`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                if (response.data.success) {
                                  toast.success('Google Meet connection successful!');
                                } else {
                                  toast.error(response.data.error || 'Google connection failed');
                                }
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to test Google connection');
                              }
                              setTestingMeeting(null);
                            }}
                            disabled={testingMeeting === 'google'}
                            data-testid="test-google-connection"
                          >
                            {testingMeeting === 'google' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                            Test
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Client ID</Label>
                        <Input
                          value={settings.google_client_id || ''}
                          onChange={(e) => setSettings({...settings, google_client_id: e.target.value})}
                          placeholder="Your Google Client ID"
                          className="bg-white/10 border-white/20"
                          data-testid="google-client-id-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showSecrets.google_client_secret ? 'text' : 'password'}
                            value={settings.google_client_secret || ''}
                            onChange={(e) => setSettings({...settings, google_client_secret: e.target.value})}
                            placeholder="Your Google Client Secret"
                            className="bg-white/10 border-white/20 pr-10"
                            data-testid="google-client-secret-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecrets({...showSecrets, google_client_secret: !showSecrets.google_client_secret})}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showSecrets.google_client_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Get credentials from the <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-neon-blue underline">Google Cloud Console</a>. 
                      Enable the Google Calendar API and create OAuth 2.0 credentials.
                    </p>
                  </div>

                  {/* Microsoft Teams Configuration */}
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-500/20">
                          <Video className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-medium">Microsoft Teams</h4>
                          <p className="text-xs text-muted-foreground">Azure AD App Registration</p>
                        </div>
                      </div>
                      {settings.teams_tenant_id && settings.teams_client_id && settings.teams_client_secret && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            setTestingMeeting('teams');
                            try {
                              const token = localStorage.getItem('token');
                              const response = await axios.get(`${API}/meetings/test-connection/teams`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              if (response.data.success) {
                                toast.success('Microsoft Teams connection successful!');
                              } else {
                                toast.error(response.data.error || 'Teams connection failed');
                              }
                            } catch (error) {
                              toast.error(error.response?.data?.detail || 'Failed to test Teams connection');
                            }
                            setTestingMeeting(null);
                          }}
                          disabled={testingMeeting === 'teams'}
                          data-testid="test-teams-connection"
                        >
                          {testingMeeting === 'teams' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                          Test
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Tenant ID</Label>
                        <Input
                          value={settings.teams_tenant_id || ''}
                          onChange={(e) => setSettings({...settings, teams_tenant_id: e.target.value})}
                          placeholder="Your Azure AD Tenant ID"
                          className="bg-white/10 border-white/20"
                          data-testid="teams-tenant-id-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Client ID</Label>
                        <Input
                          value={settings.teams_client_id || ''}
                          onChange={(e) => setSettings({...settings, teams_client_id: e.target.value})}
                          placeholder="Your Azure App Client ID"
                          className="bg-white/10 border-white/20"
                          data-testid="teams-client-id-input"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Client Secret</Label>
                        <div className="relative">
                          <Input
                            type={showSecrets.teams_client_secret ? 'text' : 'password'}
                            value={settings.teams_client_secret || ''}
                            onChange={(e) => setSettings({...settings, teams_client_secret: e.target.value})}
                            placeholder="Your Azure App Client Secret"
                            className="bg-white/10 border-white/20 pr-10"
                            data-testid="teams-client-secret-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowSecrets({...showSecrets, teams_client_secret: !showSecrets.teams_client_secret})}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showSecrets.teams_client_secret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Register an app in <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps" target="_blank" rel="noopener noreferrer" className="text-neon-blue underline">Azure Portal</a>. 
                      Add the "OnlineMeetings.ReadWrite" API permission and grant admin consent.
                    </p>
                  </div>

                  {/* Save Reminder */}
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm text-yellow-200">
                    <strong>Remember:</strong> Click "Save Settings" at the bottom of the page to save your meeting integration credentials.
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Client Portal Tab */}
            <TabsContent value="client-portal" data-testid="client-portal-tab-content">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <ExternalLink className="w-6 h-6" />
                    Client Portal Settings
                  </CardTitle>
                  <p className="text-muted-foreground">Configure how clients interact with their event portal.</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Edit Lockout Section */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Edit Lockout Period
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Prevent clients from editing playlists and timelines within a certain number of days before their event. 
                      They will still be able to view them, but editing will be disabled. You (the DJ) can always edit them.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 max-w-xs">
                        <Label htmlFor="lockout-days">Days before event to lock editing</Label>
                        <Input
                          id="lockout-days"
                          type="number"
                          min="0"
                          max="365"
                          placeholder="No lockout (leave empty)"
                          value={settings.client_edit_lockout_days || ''}
                          onChange={(e) => setSettings({ 
                            ...settings, 
                            client_edit_lockout_days: e.target.value ? parseInt(e.target.value) : null 
                          })}
                          className="bg-muted border-border"
                        />
                      </div>
                      <div className="text-sm text-muted-foreground pt-6">
                        {settings.client_edit_lockout_days > 0 ? (
                          <span className="text-amber-400">
                            Clients will be locked out {settings.client_edit_lockout_days} day{settings.client_edit_lockout_days !== 1 ? 's' : ''} before the event
                          </span>
                        ) : (
                          <span>No lockout - clients can edit until the event</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Photo Retention Section */}
                  <div className="space-y-4 pt-4 border-t border-border">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Camera className="w-5 h-5" />
                      Event Photo Retention
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Guest photos from events will be automatically deleted after this many days.
                      Before deletion, you'll receive an email with all the photos attached so you can save them.
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex-1 max-w-xs">
                        <Label htmlFor="photo-retention-days">Days to keep event photos (max 90)</Label>
                        <Input
                          id="photo-retention-days"
                          type="number"
                          min="7"
                          max="90"
                          placeholder="90"
                          value={settings.photo_retention_days || 90}
                          onChange={(e) => {
                            const val = Math.min(90, Math.max(7, parseInt(e.target.value) || 90));
                            setSettings({ ...settings, photo_retention_days: val });
                          }}
                          className="bg-muted border-border"
                          data-testid="photo-retention-input"
                        />
                      </div>
                      <div className="text-sm text-muted-foreground pt-6">
                        <span>
                          Photos older than <span className="text-foreground font-medium">{settings.photo_retention_days || 90} days</span> will be emailed to you, then removed
                        </span>
                      </div>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                      <p className="text-sm text-blue-300">
                        Before any photos are deleted, you'll receive an email with a ZIP file of all the photos. 
                        This runs automatically once a day at 3 AM EST.
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-border">
                    <Button onClick={handleSave} className="bg-neon-blue hover:bg-neon-blue/90 text-black">
                      <Save className="w-4 h-4 mr-2" />
                      Save Settings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Features Tab */}
            <TabsContent value="features" data-testid="features-tab-content">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Play className="w-6 h-6" />
                    Guest Experience Features
                  </CardTitle>
                  <p className="text-muted-foreground">Control which Guest Experience features are available in the client portal for your bookings</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 rounded-lg border border-glass-border bg-background/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(6,182,212,0.15)' }}>
                          <Music className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <p className="font-medium">Song Requests</p>
                          <p className="text-sm text-muted-foreground">Allow guests to search and request songs during events</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.feature_song_requests !== false}
                        onCheckedChange={(v) => setSettings({ ...settings, feature_song_requests: v })}
                        data-testid="toggle-song-requests"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg border border-glass-border bg-background/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.15)' }}>
                          <Camera className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium">Photo Uploads</p>
                          <p className="text-sm text-muted-foreground">Allow guests to upload and share photos during events</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.feature_photo_uploads !== false}
                        onCheckedChange={(v) => setSettings({ ...settings, feature_photo_uploads: v })}
                        data-testid="toggle-photo-uploads"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg border border-glass-border bg-background/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(234,179,8,0.15)' }}>
                          <Monitor className="w-5 h-5 text-yellow-400" />
                        </div>
                        <div>
                          <p className="font-medium">Photo Slideshow</p>
                          <p className="text-sm text-muted-foreground">Enable live photo slideshow display for event screens</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.feature_slideshow !== false}
                        onCheckedChange={(v) => setSettings({ ...settings, feature_slideshow: v })}
                        data-testid="toggle-slideshow"
                      />
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-300">
                      These settings apply company-wide. When a feature is disabled, it won't appear in the Guest Experience for any of your bookings. Individual event toggles in the client portal will still respect these master settings.
                    </p>
                  </div>

                  <Button onClick={handleSave} disabled={saving} data-testid="save-features-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Features
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Backups Tab */}
            <TabsContent value="backups" data-testid="backups-tab-content">
              <Card className="glass-card border-glass-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Database className="w-6 h-6" />
                    Database Backups
                  </CardTitle>
                  <p className="text-muted-foreground">Automated backups are emailed to you twice daily. You can also trigger a manual backup anytime.</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-4 rounded-lg border border-glass-border bg-background/50">
                    <h3 className="font-semibold mb-3">Backup Schedule</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm">12:00 PM EST (Noon)</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="text-sm">12:00 AM EST (Midnight)</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg border border-glass-border bg-background/50">
                    <h3 className="font-semibold mb-2">Backup Details</h3>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>Sent to: <span className="text-foreground">alan@dj-alan.com</span></li>
                      <li>Format: Compressed JSON (.json.gz)</li>
                      <li>Includes: All collections (bookings, contacts, contracts, payments, playlists, etc.)</li>
                      <li>Excludes: Music Library (can be re-uploaded separately)</li>
                    </ul>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      data-testid="trigger-backup-btn"
                      onClick={async () => {
                        try {
                          toast.info('Generating backup...');
                          const token = localStorage.getItem('token');
                          const res = await axios.post(`${API}/backup/trigger`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(res.data.message || 'Backup email sent!');
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Backup failed');
                        }
                      }}
                      className="bg-primary hover:bg-primary/80"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Backup Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Tab - Data Export & Cancellation */}
            {!isEmployee && (
              <TabsContent value="account">
                <Card className="glass-card border-glass-border mb-6">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl flex items-center gap-2">
                      <UserX className="w-6 h-6" />
                      Account Management
                    </CardTitle>
                    <p className="text-muted-foreground">Export your data or manage your account cancellation.</p>
                  </CardHeader>
                </Card>
                <AccountCancellation 
                  user={user}
                  accountStatus={{
                    account_status: user?.account_status,
                    scheduled_deletion_date: user?.scheduled_deletion_date,
                    days_until_deletion: user?.days_until_deletion
                  }}
                  onStatusChange={() => {
                    // Refresh user data
                    window.location.reload();
                  }}
                />
              </TabsContent>
            )}
          </Tabs>

          {/* Save Button */}
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-neon-blue hover:bg-neon-blue/80 text-black font-semibold px-8"
              data-testid="save-settings-btn"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
